// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Utils
import platformSingleton from '../platform/platformSingleton';
import { asObject, prop } from '../../common/utils/immutableHelper';
import { hasChanged } from '../utils/react/propsComparisons';
import { gridPointsToPixels, pixelsToGridPoints } from '../utils/grid/gridUtils';
import measurementsRegistry from '../components/measurementsStore/measurementsRegistry';
import {
    clearBoardsFromCanvasReturnState,
    getCanvasReturnState,
    saveCanvasReturnState,
} from './canvasReturnStateService';
import { getScrollableAreaSizeFromContentRect } from './zoom/zoomScrollAreaSizeUtils';
import { getContentVisibleScrollPosition } from './utils/scrollToContentUtils';

// Actions
import { setCanvasSize, setCanvasZoomTransformation } from './store/canvasActions';

// Selector
import { getCurrentUser } from '../user/currentUserSelector';
import { getTopLeftElementPositionDetails } from '../element/selectors/elementCanvasPositionSelector';

// Measurements
import measurementsConnect from '../components/measurementsStore/measurementsConnect';
import { getCanvasElementMeasurementsBoundingRect } from '../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Constants
import { MAXIMUM_POSITION_VALUE } from '../../common/elements/utils/elementPositionUtils';
import { CANVAS_ELEMENT_PADDING_STANDARD } from './utils/canvasSizeUtils';
import { LegacyHybridUseCase, MilanoteApplicationMode } from '../../common/platform/platformTypes';

const currentBoardIdChanged = hasChanged('currentBoardId');

const mapStateToProps = createStructuredSelector({
    currentUser: getCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSetCanvasSize: (canvasSize) => dispatch(setCanvasSize({ canvasSize })),
    dispatchSetCanvasZoomTransformation: (transformation) => dispatch(setCanvasZoomTransformation(transformation)),
    dispatchGetTopLeftElementPositionDetails: () =>
        dispatch((_, getState) => getTopLeftElementPositionDetails(getState())),
});

const mapMeasurementsDispatchToProps = (dispatch) => ({
    dispatchGetCanvasElementsBoundingRect: () =>
        dispatch((_, getState) => getCanvasElementMeasurementsBoundingRect(getState())),
});

const getDefaultZoomScaleForHybridUseCase = () => {
    switch (platformSingleton.legacyHybridUseCase) {
        case LegacyHybridUseCase.ANDROID_BOARD_LIST:
        case LegacyHybridUseCase.IPAD_OS:
        default:
            return 1.0;
    }
};

const DEFAULT_ZOOM_SCALE = getDefaultZoomScaleForHybridUseCase();

/**
 * Decorator that saves and restores the canvas state when the user:
 * 1. Navigate to another board
 * 2. Navigate away from Milanote (either by closing or refreshing the page)
 * 3. Navigate to another page within Milanote, where the canvas is not visible / is replaced
 *    - An example of this is going from "My Boards" to "Quick Notes" on mobile
 */
export default (DecoratedComponent) => {
    @connect(mapStateToProps, mapDispatchToProps)
    @measurementsConnect(null, mapMeasurementsDispatchToProps)
    class canvasReturnStateDecorator extends React.Component {
        componentDidMount() {
            this.restoreCanvas(this.props);

            // Save canvas return sate when user navigate away from Milanote
            // e.g. either by closing or refreshing the page
            window.addEventListener('visibilitychange', this.onVisibilityChange);
        }

        componentWillUnmount() {
            window.removeEventListener('visibilitychange', this.onVisibilityChange);

            // Save canvas return state when user navigate to another page within Milanote,
            // where the canvas is not visible / is replaced
            this.saveCanvasReturnState(this.props);
        }

        componentDidUpdate(prevProps) {
            // Save canvas return state when user navigate to another board
            if (currentBoardIdChanged(prevProps, this.props)) {
                this.saveCanvasReturnState(prevProps);
                this.restoreCanvas(this.props);
            }
        }

        onVisibilityChange = () => {
            if (document.hidden) {
                this.saveCanvasReturnState(this.props);
            }
        };

        saveCanvasReturnState = (props) => {
            const {
                currentBoardId,
                canvasSize,
                gridSize,
                zoomScale,
                zoomTranslationPx,
                dispatchGetCanvasElementsBoundingRect,
            } = props;

            const canvasViewport = measurementsRegistry.getCanvasViewportDomElement();

            // If it's a fresh page load, no need to save return location
            if (!currentBoardId) return;
            // If there's no canvas element, we can't save the return location
            if (!canvasViewport) return;

            const elementsBoundingRect = dispatchGetCanvasElementsBoundingRect();

            const scrollTop = pixelsToGridPoints(canvasViewport.scrollTop, gridSize) / zoomScale;
            const scrollLeft = pixelsToGridPoints(canvasViewport.scrollLeft, gridSize) / zoomScale;

            const widthPx = prop('width', canvasSize);
            const heightPx = prop('height', canvasSize);

            const maxWidthPx = elementsBoundingRect?.right
                ? elementsBoundingRect.right + CANVAS_ELEMENT_PADDING_STANDARD.x * gridSize
                : Infinity;
            const maxHeightPx = elementsBoundingRect?.bottom
                ? elementsBoundingRect.bottom + CANVAS_ELEMENT_PADDING_STANDARD.y * gridSize
                : Infinity;

            const widthToStorePx = Math.min(widthPx, maxWidthPx);
            const heightToStorePx = Math.min(heightPx, maxHeightPx);

            const width = pixelsToGridPoints(widthToStorePx, gridSize);
            const height = pixelsToGridPoints(heightToStorePx, gridSize);

            const isDefaultReturnState = scrollTop === 0 && scrollLeft === 0 && zoomScale === DEFAULT_ZOOM_SCALE;

            // If the width or height is more than the maximum position value, something has likely gone wrong
            // so don't save the state
            const isObviouslyIncorrectCanvasSize = width > MAXIMUM_POSITION_VALUE || height > MAXIMUM_POSITION_VALUE;

            const shouldClearCanvasReturnState = isDefaultReturnState || isObviouslyIncorrectCanvasSize;

            if (shouldClearCanvasReturnState) {
                clearBoardsFromCanvasReturnState(currentBoardId);
                return;
            }

            saveCanvasReturnState(currentBoardId, {
                scrollTop,
                scrollLeft,
                width,
                height,
                zoomScale,
                zoomTranslationPx,
            });
        };

        restoreCanvas = (props) => {
            const {
                currentBoardId,
                gridSize,
                canvasElementIds,
                dispatchSetCanvasSize,
                dispatchSetCanvasZoomTransformation,
                dispatchGetTopLeftElementPositionDetails,
                platformDetails,
            } = props;

            if (!this.canvasViewport) return;

            const currentBoardReturnState = getCanvasReturnState(currentBoardId);

            const storedWidthGu = prop('width', currentBoardReturnState) || 0;
            const storedHeightGu = prop('height', currentBoardReturnState) || 0;

            const isObviouslyIncorrectCanvasSize =
                storedWidthGu > MAXIMUM_POSITION_VALUE || storedHeightGu > MAXIMUM_POSITION_VALUE;

            if (isObviouslyIncorrectCanvasSize) {
                clearBoardsFromCanvasReturnState(currentBoardId);
                return;
            }

            // Restore canvas size
            const canvasDocumentSize = {
                width: gridPointsToPixels(storedWidthGu, gridSize),
                height: gridPointsToPixels(storedHeightGu, gridSize),
            };

            dispatchSetCanvasSize(canvasDocumentSize);

            // Restore zoom state
            const zoomScale = prop('zoomScale', currentBoardReturnState) || DEFAULT_ZOOM_SCALE;
            const zoomTranslation = asObject(prop('zoomTranslationPx', currentBoardReturnState)) || { x: 0, y: 0 };

            // Restore scroll position
            const scrollTop = gridPointsToPixels(prop('scrollTop', currentBoardReturnState) || 0, gridSize) * zoomScale;
            const scrollLeft =
                gridPointsToPixels(prop('scrollLeft', currentBoardReturnState) || 0, gridSize) * zoomScale;

            // If we need to scroll the canvas, ensure that the scrollable area has a default size to start with
            // Otherwise, use a 0 size scroll area and allow the CanvasScrollArea to size itself exactly once the
            //  content sizes are known
            const scrollAreaSize =
                scrollTop || scrollLeft
                    ? getScrollableAreaSizeFromContentRect(canvasDocumentSize, zoomScale, zoomTranslation)
                    : { width: 0, height: 0 };

            dispatchSetCanvasZoomTransformation({
                scale: zoomScale,
                translation: zoomTranslation,
                scrollAreaSize,
            });

            requestAnimationFrame(() => {
                if (!this.canvasViewport) return;

                let canvasScrollPos = { scrollTop, scrollLeft };
                const shouldScrollToContent = platformDetails.appMode === MilanoteApplicationMode.mobile;

                if (shouldScrollToContent) {
                    canvasScrollPos = getContentVisibleScrollPosition(
                        canvasScrollPos,
                        canvasElementIds,
                        dispatchGetTopLeftElementPositionDetails,
                        zoomScale,
                    );
                }

                this.canvasViewport.scrollTop = canvasScrollPos.scrollTop;
                this.canvasViewport.scrollLeft = canvasScrollPos.scrollLeft;
            });
        };

        scrollDecoratorCanvasViewportRef = (c) => {
            this.canvasViewport = c;
        };

        render() {
            return (
                <DecoratedComponent
                    {...this.props}
                    scrollDecoratorCanvasViewportRef={this.scrollDecoratorCanvasViewportRef}
                />
            );
        }
    }

    canvasReturnStateDecorator.propTypes = {
        currentBoardId: PropTypes.string,
        currentUser: PropTypes.object,

        canvasSize: PropTypes.object,
        gridSize: PropTypes.number,
        zoomScale: PropTypes.number,
        zoomTranslationPx: PropTypes.object,

        dispatchSetCanvasSize: PropTypes.func,
        dispatchSetCanvasZoomTransformation: PropTypes.func,
        dispatchGetCanvasElementsBoundingRect: PropTypes.func,
    };

    return canvasReturnStateDecorator;
};
